import {QuoteContainer} from "../QuoteContainer";
import {HardwareGeneric} from "../../shopdrawing/hardware/HardwareGeneric";
import {HardwareSwitcher} from "./HardwareSwitcher";
import {TransferProducts} from "./TransferProducts";
import {EventEmitter} from "nate-react-api-helpers";
import {Grid, Tab, Tabs} from "@mui/material";
import {useTabQueryParam} from "../../../../misc/userpref/useTabQueryParam";
import {HardwareScheduleBtn} from "./HardwareScheduleBtn";
import {HardwarePhaseFilter} from "./HardwarePhaseFilter";
import {useReSequence} from "./HardwareReSequence";
import {HardwareViewMode, HardwareViewModeProvider, ShowByUsSuppliers} from "./HardwareViewMode";
import {CleanupGroups} from "./CleanupGroups";
import React, { useState } from "react";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {TabWrapper} from "../../shopdrawing/TabWrapper";
import {HardwareMisc} from "../../shopdrawing/hardware/HardwareMisc";
import {PausePricingCover} from "../pricing/PausePricingCover";
import {KeyingNotesModal} from "./KeyingNotes";
import { SmallGreyButton } from "../../../../misc/SmallGreyButton";
import {MenuButton} from "../../../logistics/purchasing/MenuButton";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {ManageProjectSuppliers} from "../../shopdrawing/hardware/ManageProjectSuppliers";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {useConfirm} from "../../../../misc/Confirm";
import {useBackgroundActionSnack} from "../../manage/sov/UseBackgroundTask";
import {api} from "../../../../api/API";

export const QuoteHwForceUpdate = new EventEmitter<number>();

// Rendered inside QuoteContainer so the QuoteContext provider is in scope. useReSequence
// resolves useHwAPI to api.quoteHardware here; calling it above QuoteContainer would bind
// the production api and post to /api/hardware/re-sequence instead.
function QuoteHardwareActions(props: { tab: string }) {
    const reSequence = useReSequence({
        onDone: () => QuoteHwForceUpdate.emit(Date.now())
    });

    const [showCleanupGroups, setShowCleanupGroups] = useState(false);
    const [showKeyingNotes, setShowKeyingNotes] = useState(false);
    const [showSuppliers, setShowSuppliers] = useState(false);

    const project = useProjectId();
    const alternative = useAlternative();
    const confirmCtx = useConfirm();

    const forceRecalculate = useBackgroundActionSnack({
        callback: (input: {project: number; alternative: number}) => api.quoteHardware.forceRecalculate(input),
        successMessage: "Hardware recalculated",
        onDone: () => QuoteHwForceUpdate.emit(Date.now()),
    });

    return (
        <PausePricingCover blank>
            {props.tab === "schedule" && <Grid container spacing={1} alignItems="center">
                <Grid item>
                    <TransferProducts />
                </Grid>
                <Grid item>
                    <HardwareScheduleBtn />
                </Grid>
                <Grid item>
                    <HardwarePhaseFilter />
                </Grid>
                <Grid item>
                    <MenuButton
                        renderButton={p => {
                            return <SmallGreyButton endIcon={<ArrowDropDownIcon />} {...p} />
                        }}
                        options={[
                            {
                                name: "Cleanup Groups",
                                onClick: () => setShowCleanupGroups(true)
                            },
                            {
                                name: "Keying Notes",
                                onClick: () => setShowKeyingNotes(true)
                            },
                            {
                                name: "Resequence",
                                onClick: () => {
                                    reSequence();
                                }
                            },
                            {
                                name: "Supplied Categories",
                                onClick: () => setShowSuppliers(true)
                            },
                            {
                                name: "Force Recalculate",
                                onClick: async () => {
                                    const ok = await confirmCtx.confirm("Recalculate all hardware in this quote? This runs in the background and may take a few minutes.");
                                    if(!ok) return;
                                    forceRecalculate.callback({project, alternative});
                                }
                            },
                        ]}>Actions</MenuButton>

                    {showKeyingNotes && <KeyingNotesModal onClose={() => setShowKeyingNotes(false)} />}
                    {showCleanupGroups && <CleanupGroups onDone={() => setShowCleanupGroups(false)} />}
                    {showSuppliers && <ManageProjectSuppliers onDone={() => {
                        setShowSuppliers(false);
                        QuoteHwForceUpdate.emit(Date.now());
                    }} />}
                </Grid>
            </Grid>}
        </PausePricingCover>
    )
}

export function QuoteHardware() {
    const cacheBustKey = EventEmitter.reactValue(QuoteHwForceUpdate);
    const [tab, setTab, dt] = useTabQueryParam({page: "quote.hardware", urlKey: "t", fallback: "schedule", allowed: ["schedule", "misc"]});

    return (
        <HardwareViewModeProvider>
            <QuoteContainer
                name={<HardwareSwitcher />}
                actions={<QuoteHardwareActions tab={tab} />}
                overrideRightActions={<>
                    <div style={{display: "flex", flexDirection: "row"}}>
                        <HardwareViewMode />
                        <ShowByUsSuppliers />
                    </div>
                </>}
            >

                <PausePricingCover>
                    <TabWrapper>
                        <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                            <Tab label="Schedule" value="schedule" {...dt.tabProps("schedule")} />
                            <Tab label="Misc" value="misc" {...dt.tabProps("misc")} />
                        </Tabs>
                        {dt.controls}
                    </TabWrapper>

                    {tab === "schedule" && <HardwareGeneric key={cacheBustKey?.toString() || "-"} />}
                    {tab === "misc" && <HardwareMisc />}
                </PausePricingCover>
            </QuoteContainer>
        </HardwareViewModeProvider>
    )
}